export type RecorderKind = 'audio' | 'video'

const MIME_CANDIDATES: Record<RecorderKind, readonly string[]> = {
  audio: ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/mp4', 'audio/webm'],
  video: ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'],
}

export function selectSupportedMimeType(
  kind: RecorderKind,
  isSupported: (mime: string) => boolean,
): string | undefined {
  return MIME_CANDIDATES[kind].find(isSupported)
}

export function extensionForMime(mime: string): string {
  if (mime.includes('ogg')) return 'ogg'
  if (mime.includes('mp4')) return 'mp4'
  return 'webm'
}

export function resolveRecorderMime(
  kind: RecorderKind,
  chunkTypes: readonly string[],
  currentRecorderMime: string,
  fallbackMime = '',
): string | null {
  const declared = chunkTypes.map((value) => value.trim().toLowerCase()).filter(Boolean)
  const first = declared[0]
  if (first && declared.some((value) => value !== first)) return null
  const current = currentRecorderMime.trim().toLowerCase()
  const fallback = fallbackMime.trim().toLowerCase()
  const resolved = first || current || fallback
  if (!resolved || !resolved.startsWith(`${kind}/`) || !/^(?:audio|video)\/(?:webm|ogg|mp4)(?:;|$)/.test(resolved)) return null
  return resolved
}

export function formatDuration(totalSeconds: number): string {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds))
  const minutes = Math.floor(safeSeconds / 60)
  return `${String(minutes).padStart(2, '0')}:${String(safeSeconds % 60).padStart(2, '0')}`
}

export type RecorderError = 'unsupported' | 'permissionDenied' | 'deviceMissing' | 'busy' | 'genericError'

export function classifyMediaError(error: unknown): RecorderError {
  if (!(error instanceof DOMException)) return 'genericError'
  if (error.name === 'NotAllowedError' || error.name === 'SecurityError') return 'permissionDenied'
  if (error.name === 'NotFoundError' || error.name === 'OverconstrainedError') return 'deviceMissing'
  if (error.name === 'NotReadableError' || error.name === 'AbortError') return 'busy'
  return 'genericError'
}

export function stopStream(stream: MediaStream | null): void {
  stream?.getTracks().forEach((track) => track.stop())
}

export class ActiveTimeTracker {
  private accumulatedMs = 0
  private activeSince: number | null = null

  start(now: number): void {
    this.accumulatedMs = 0
    this.activeSince = now
  }

  pause(now: number): void {
    if (this.activeSince === null) return
    this.accumulatedMs += Math.max(0, now - this.activeSince)
    this.activeSince = null
  }

  resume(now: number): void {
    if (this.activeSince !== null) return
    this.activeSince = now
  }

  elapsed(now: number): number {
    return this.accumulatedMs + (this.activeSince === null ? 0 : Math.max(0, now - this.activeSince))
  }

  remaining(limitMs: number, now: number): number {
    return Math.max(0, limitMs - this.elapsed(now))
  }
}

export function addClonedAudioTrack(input: MediaStream, output: MediaStream): void {
  const audioTrack = input.getAudioTracks()[0]
  if (!audioTrack) return
  const cloned = audioTrack.clone()
  try {
    output.addTrack(cloned)
  } catch (cause) {
    cloned.stop()
    throw cause
  }
}

export function createCapturedMediaStream(
  capture: () => MediaStream,
  input: MediaStream,
): MediaStream {
  let output: MediaStream | null = null
  try {
    output = capture()
    addClonedAudioTrack(input, output)
    return output
  } catch (cause) {
    stopStream(output)
    throw cause
  }
}
